<template>
  <div>
    <title-row
      :title="page.title"
      :breadcrumb-items="[
        { text: 'Kulcsok és eszközök', to: { name: 'AssetList' } },
        page.breadcrumbItem,
      ]">
      <template #buttons>
        <title-row-button v-if="hasRight('KEY_WRITE')" @click="save" title="Mentés">
          <v-icon dark>mdi-content-save</v-icon>
          Mentés
        </title-row-button>
      </template>
    </title-row>

    <loading-overlay v-if="loading" />

    <v-row>
      <v-col :cols="12">
        <v-card class="rounded-xl">
          <v-card-title>
            <v-icon left>mdi-key</v-icon>
            Alapadatok
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col :cols="12" :md="6">
                <v-text-field
                  label="Megnevezés"
                  :error-messages="errors.name"
                  v-model="form.name"
                  filled />

                <v-text-field
                  label="RFID"
                  filled
                  :error-messages="errors.rfid"
                  v-model="form.rfid"
                  :hint="readerDisabled ? '' : 'Érintse a kártyát az olvasóhoz'" />

                <v-autocomplete
                  label="Kulcsszekrények"
                  :items="asset_cabinets"
                  item-text="name"
                  item-value="id"
                  v-model="form.cabinet_ids"
                  :value-comparator="$config.comparators.isEqual"
                  filled
                  multiple
                  chips
                  deletable-chips />
              </v-col>
              <v-col :cols="12" :md="6">
                <v-textarea
                  label="Megjegyzés"
                  name="name"
                  v-model="form.remark"
                  filled
                  auto-grow
                  :rows="5" />

                <v-swatches
                  v-model="form.color"
                  inline
                  :swatches="[
                    '#FF6D00',
                    '#D50000',
                    '#9C27B0',
                    '#673AB7',
                    '#3F51B5',
                    '#2196F3',
                    '#03A9F4',
                    '#00BCD4',
                    '#009688',
                    '#4CAF50',
                    '#8BC34A',
                    '#CDDC39',
                    '#FFEB3B',
                    '#FFC107',
                    '#FF9800',
                    '#795548',
                    '#9E9E9E',
                    '#000000',
                    '#FFFFFF',
                    '',
                  ]"
                  shapes="squares"
                  show-border
                  :spacing-size="4"
                  :swatch-size="32" />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col :cols="12">
        <v-card :loading="groupsLoading" class="rounded-xl">
          <v-card-title>
            <v-icon left>mdi-group</v-icon>
            Engedélyezett csoportok
          </v-card-title>
          <v-card-text>
            <v-autocomplete
              label="Csoportok"
              :items="groups"
              v-model="form.groups"
              filled
              multiple
              item-text="name"
              item-value="element_id"
              :value-comparator="$config.comparators.isEqual"
              return-object
              hide-details
              chips
              deletable-chips>
              <template v-slot:selection="{ item, index }">
                <v-chip :to="{ name: 'GroupEditor', params: { id: item.element_id } }" target="_blank">
                  <span>{{ item.name }}</span>
                  <v-btn
                    icon
                    x-small
                    @click.prevent="form.groups.splice(index)"
                    class="ml-2">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-chip>
              </template>
            </v-autocomplete>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col :cols="12">
        <v-card :loading="loading" class="rounded-xl">
          <v-card-title>
            <v-icon left>mdi-cards</v-icon>
            Engedélyezett kártyák
          </v-card-title>
          <v-card-text>
            <v-two-panel-multiselect
              :headers="[
                { text: 'Kártyaszám', value: 'card_number' },
                { text: 'Birtokos', value: 'owner_name' },
              ]"
              :options="{ sortBy: ['card_number'] }"
              :items="cards"
              v-model="form.cards"
              item-value="element_id">
              <template #header="{ search }">
                <v-card outlined class="mb-6">
                  <v-card-title>
                    <v-icon left>mdi-filter</v-icon>
                    Keresés
                  </v-card-title>
                  <v-card-text>
                    <v-row>
                      <v-col :cols="12" :md="6" :lg="3">
                        <v-text-field label="Kártyatulajdonos" v-model="search.owner_name" hide-details />
                      </v-col>
                      <v-col :cols="12" :md="6" :lg="3">
                        <v-text-field label="Kártyaszám" v-model="search.card_number" hide-details />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </template>
            </v-two-panel-multiselect>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import VSwatches from 'vue-swatches';
import 'vue-swatches/dist/vue-swatches.css';

export default {
  components: { VSwatches },

  data() {
    return {
      form: {
        name: '',
        remark: '',
        cards: [],
        groups: [],
        cabinet_ids: [],
        rfid: '',
      },

      cards: [],

      readerDisabled: true,
      socket: false,
      socketError: false,

      errors: {},
      loading: false,

      groupsLoading: false,
    };
  },

  async mounted() {
    this.watchCardReader();
    this.fetchAll();
  },

  methods: {
    async fetchBuildings() {
      const response = await this.$http.get('buildings/list');
      this.buildings = response.buildings;
    },

    async fetchAll() {
      this.loading = true;
      await Promise.all([this.fetchAsset(), this.fetchBuildings()]);
      this.loading = false;
    },

    async fetchAsset() {
      try {
        const response = await this.$http.get(`assets/details/${this.$route.params.id || 0}`);
        this.form = response.asset;
        this.cards = response.cards;
      } catch (e) {
        console.error(e);
      }
    },

    async save(event) {
      try {
        this.errors = {};
        this.loading = true;
        this.form.color = this.form?.color?.hexa || this.form?.color;
        const response = await this.$http.post('assets/save/', this.form);
        if (this.lodash.size(response.errors) > 0) {
          this.errors = response.errors;
          return;
        }

        if (response.status === 'OK') {
          this.$dialog.notify.success('Sikeres mentés');
          if (this.$route.name === 'NewAsset') {
            this.$router.push({
              name: 'AssetEditor',
              params: { id: response.asset.element_id },
            });
          } else {
            this.fetchAll();
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    async watchCardReader() {
      if (this.socket.readyState === WebSocket.OPEN) {
        this.socket.close();
      }

      try {
        this.socket = new WebSocket('wss://kmr-reader.uninet.hu:8080');

        this.socket.onopen = async (event) => {
          this.readerDisabled = false;
        };

        this.socket.onmessage = async (event) => {
          this.form.rfid = event.data;
          this.form = Object.assign({}, this.form, { rfid: event.data });
        };
      } catch (err) {
        console.error(err);
        this.reader = false;
      } finally {
        this.loading = false;
      }
    },
  },

  asyncComputed: {
    groups: {
      async get() {
        this.groupsLoading = true;
        const response = await this.$http.post('groups/list', {
          type_codes: ['KARTYA'],
        });
        this.groupsLoading = false;
        return response.groups;
      },
      default: [],
    },

    asset_cabinets: {
      async get() {
        const response = await this.$http.post('asset-cabinets/list', {
          nolimit: true,
        });
        return response.asset_cabinets;
      },
      default: [],
    },

    // cards: {
    //   async get() {
    //     this.cardsLoading = true;
    //     const response = await this.$http.post('cards/list', { limit: 50000 });
    //     this.cardsLoading = false;
    //     return response.cards;
    //   },
    //   default: [],
    // },
  },

  computed: {
    page() {
      switch (this.$route.name) {
        case 'NewAsset':
          return {
            isNew: true,
            title: 'Új kulcs/eszköz',
            breadcrumbItem: {
              text: 'Új kulcs/eszköz',
              disabled: true,
            },
          };
        case 'AssetEditor':
          return {
            isNew: false,
            title: `${this.form.name ?? 'Betöltés...'}`,
            breadcrumbItem: {
              text: this.form.name,
              disabled: true,
            },
          };
      }
      return {};
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .vue-swatches__container.vue-swatches--inline {
  background: none !important;
}
</style>
